import { UserService } from './../../core/user/services/user/user.service';
import { AfterContentInit, Directive, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { IAppState } from '@app/store';
import * as fromUser from '@app/core/user/store/reducers';
import { IBlock, IUserData } from '@vestiaire/vc-angular-lib';
import { CatalogConfig } from '@app/shared/model/interfaces/catalog';
import { LocaleService } from '@app/core/i18n';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { UserUtilsService } from '@app/library/services/user-utils.service';
import { SearchIndex } from '@app/shared/components/catalog/services/instantsearch.service';
import { PromoClickEvent, PromoViewEvent } from '@vestiaire/vc-angular-lib/lib/page/track-block';
import { PromoViewed, PromoClicked } from '@app/+landing-cms/actions';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';
import { UrlParser, RouteState } from '@app/core/search/routing';
import { SearchStateMappingService } from '@app/core/search/services';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { isPlatformServer } from '@angular/common';
import { translationKey } from '@helpers/translation';
import { TrackMerchBlock } from '@app/+catalog/store/actions';
import { Product } from '@vestiaire/api-sdk-angular';
import { environment } from '@env/environment';
import { getUser } from '@app/core/user/store/reducers';

export interface ISpecialCatalog {
  arrayIndex: number;
  configCatalog: CatalogConfig;
}
export enum RecentlyViewed {
  MIN_ITEMS = 4
}
@Directive()
export abstract class BasePageComponentDirective implements AfterContentInit, OnDestroy {
  public updatedBlockList: IBlock[];
  public configCatalogArray: CatalogConfig[] = [];
  public likedItem: string[];
  public isMdUp = false;
  public localeLang = '';
  public userData: IUserData;
  public incrementBlockProductDisplay: number;
  public commentsBlock$ = new Subject<IBlock>();
  public recentlyViewedProducts: Array<Product> = [];
  public recently_viewed_feature_flag = environment.feature.recently_viewed_block || false;
  public recentlyViewedBlock = {
    displayBlock: false,
    arrangement: '',
    maxItems: 0
  };
  public storeListener: Subscription;

  constructor(
    protected readonly store$: Store<IAppState>,
    protected readonly localeService: LocaleService,
    protected readonly platformService: PlatformService,
    protected readonly userUtilsService: UserUtilsService,
    protected readonly helpersService: HelpersService,
    protected readonly searchStateMappingService: SearchStateMappingService,
    protected readonly transferState: TransferState,
    protected readonly platformId: Object,
    protected readonly pageType: string,
    protected readonly userService: UserService
  ) {
    this.store$.pipe(select(fromUser.getLikedItems)).subscribe(
      productsLiked => productsLiked && (this.likedItem = productsLiked.products),
      error => console.log(error)
    );

    this.localeLang = this.localeService.getLanguage();
    this.userData = this.userUtilsService.prepareUserData();
  }

  ngAfterContentInit(): void {
    if (this.platformService.isBrowser) {
      this.store$.select('device').subscribe(
        res => res && (this.isMdUp = res.isMdUp),
        err => console.log(err)
      );
    }
  }

  ngOnDestroy() {
    this.storeListener?.unsubscribe();
  }

  public parseBlockList(blockList: IBlock[]): void {
    this.incrementBlockProductDisplay = 1;
    let hasOneTopBanner = false;
    blockList.forEach((block, index) => {
      switch (block.type) {
        case 'banner':
          if (index === 0) {
            this.helpersService.hasBanner.next(true);
            hasOneTopBanner = true;
            if (this.pageType === 'home') {
              block = this._formatBanner(block);
            }
          }
          break;
        case 'product':
          this._fetchProduct(block, index);
          break;
        case 'recently-viewed':
          this._fetchLocallyStoredProducts(block);
          break;
        case 'comment':
          this.commentsBlock$.next(block);
          break;
        default:
          break;
      }
    });

    if (!hasOneTopBanner) {
      this.helpersService.hasBanner.next(false);
    }

    // Trigger change in promise to avoid change detection issue
    // -> differ check on the next iteration loop
    Promise.resolve(null).then(() => (this.updatedBlockList = blockList));
  }

  private _formatBanner(bannerBlock: IBlock): IBlock {
    const key = makeStateKey<string>(`@@${translationKey.BannerTitle}`);
    let text = '';

    if (isPlatformServer(this.platformId)) {
      text = this.localeService.getTranslationValue(translationKey.BannerTitle);
      this.transferState.set(key, text);
    }

    if (!isPlatformServer(this.platformId)) {
      if (this.transferState.hasKey(key)) {
        text = this.transferState.get(key, null);
      } else {
        text = this.localeService.getTranslationValue(translationKey.BannerTitle);
      }
    }

    const contentToInsert = { isTitle: true, show: true, target: '_self', text };
    if (bannerBlock.body && bannerBlock.body.content) {
      bannerBlock.body.content.unshift(contentToInsert);
    }
    return bannerBlock;
  }

  private _fetchProduct(productBlock: IBlock, index: number): void {
    const facetFilter = [];
    const numericFilters = [];

    if (productBlock.body && productBlock.body && productBlock.body.link && productBlock.body.link.path) {
      const url = productBlock.body.link.path;
      const urlParser = new UrlParser(url);

      const params = urlParser.getParams();
      const path = urlParser.getPathname();

      // Try to deal with /c/ catalog url
      if (path.includes('/c/')) {
        const matcher = path.match(/(\d)\d+/g);
        if (!matcher && matcher.length <= 0) {
          return;
        }

        facetFilter.push(`campaigns:${matcher[matcher.length - 1]}`);
      } else {
        // TODO: for now its only for "old" catalog page, think how to include new catalog pages like : /g/, etc.
        // Using catalogLinksWithoutLanguage instead of catalogLinks.{i18n} because only one link for all language
        facetFilter.push(`catalogLinksWithoutLanguage:${path}`);

        const routeState: RouteState = {
          originalUrl: url,
          pathname: path,
          params
        };
        const refinementsByFilterType = this.searchStateMappingService.routeToState(routeState);
        Object.keys(refinementsByFilterType).forEach(type => {
          if (type === 'refinementList') {
            const list = refinementsByFilterType[type];
            Object.keys(list).forEach(filter => {
              const res = list[filter].map(e => `${filter}:${e}`);
              facetFilter.push(res);
            });
          } else if (type === 'range') {
            const range = refinementsByFilterType[type];
            Object.keys(range).forEach(filter => {
              const [min, max] = range[filter].split(':');
              if (min) {
                numericFilters.push(`${filter}>=${min}`);
              }
              if (max) {
                numericFilters.push(`${filter}<=${max}`);
              }
            });
          } else if (type === 'toggle') {
            const toggles = refinementsByFilterType[type];
            Object.keys(toggles).forEach(toggle => {
              facetFilter.push(`${toggle}:${toggles[toggle] ? 1 : 0}`);
            });
          }
        });
      }
    }

    // Uggly fix to have more than one product block in the homepage
    // sorry for doing this but whith the current implementation of vc-catalog
    // I can't do better
    productBlock['indexToDisplay'] = this.incrementBlockProductDisplay++;

    this.configCatalogArray.push({
      indexName: SearchIndex.Recent,
      hitsPerPage: 20,
      withFilters: false,
      withPagination: false,
      facetFilter,
      numericFilters,
      tracking: productBlock.tracking ? productBlock.tracking : ''
    });
  }

  private _fetchLocallyStoredProducts(productBlock: IBlock) {
    // conditional showing of recently viewed block.
    // when used in ngx, rendering depends on feature flag and min number of product(4)
    // but always show when used for CMS-admin
    // these flags are used in vc-angular-lib
    productBlock['from-ngx'] = true;
    productBlock['recently_viewed_feature_flag'] = this.recently_viewed_feature_flag;

    this.storeListener = this.store$.pipe(select(getUser)).subscribe({
      next: user => {
        if (user) {
          this.getRecentlySeenProducts(productBlock);
        } else {
          this.recentlyViewedBlock.displayBlock = false;
          if (this.updatedBlockList) {
            const index = this.updatedBlockList.findIndex(block => block.type === 'recently-viewed');
            if (index !== -1) {
              this.updatedBlockList[index]['displayBlock'] = false;
            }
          }
        }
      }
    });
  }

  getRecentlySeenProducts(productBlock: IBlock) {
    this.userService
      .getRecentlySeenProducts()
      .then((products: Array<Product>) => {
        this.recentlyViewedProducts = this.mapProductPicturePath(products);
        if (this.recentlyViewedProducts.length >= RecentlyViewed.MIN_ITEMS) {
          productBlock['displayBlock'] = true;
          this.recentlyViewedBlock = {
            displayBlock: productBlock['displayBlock'],
            arrangement: productBlock['arrangement'],
            maxItems: productBlock?.body?.maxItems
          };
        }
      })
      .catch(err => {
        console.error('No data For getRecentlySeenProducts', err);
      });
  }

  mapProductPicturePath(data: Array<Product>): Array<Product> {
    return data.map(product => {
      // Do not modify data: it's the same value that gets transfered in server state
      // This is a workaround
      if (product.pictures[0].path.startsWith('/produit/')) {
        return product;
      }
      product.pictures[0].path = '/produit/' + product.pictures[0].path;
      return product;
    });
  }

  public onTrackClick({ detail }: { detail: PromoClickEvent }): void {
    this.store$.dispatch(new PromoClicked(detail));
    // To be able to track in the catalogue that users came from a merchandising block on the home/landing page,
    // the block needs to be identified in the context of the catalogue.
    if (detail.promotions[0].name?.includes('_merchandising')) {
      const cmsBlockId = detail.promotions[0].creative.substr(0, 24); // retrieve cms merch block id
      this.store$.dispatch(new TrackMerchBlock({ cmsBlockId, isMerchBlock: true }));
    }
  }

  public onTrackView({ detail }: { detail: PromoViewEvent }): void {
    this.store$.dispatch(new PromoViewed(detail));
  }
}
